import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApplicantName } from '../utils/visaApplicationFilters';
import { formatStatusLabel } from '../utils/visaApplicationStatus';
import {
  getFormDisplayEntries,
  isFormFilled,
  needsFormLabelResolution,
} from '../utils/visaApplicationData';
import { getVisaFormLabelsUrl } from '../utils/visaApplicationHistoryApi';

const VisaApplicationDetailModal = ({ application, onClose, extraFields = [] }) => {
  const [formEntries, setFormEntries] = useState([]);
  const [loadingLabels, setLoadingLabels] = useState(false);

  const filled =
    application?.form_filled ?? isFormFilled(application?.customer_visa_application_data);

  useEffect(() => {
    if (!application || !filled) {
      setFormEntries([]);
      return;
    }

    const initialEntries = getFormDisplayEntries(application.customer_visa_application_data);
    setFormEntries(initialEntries);

    if (!needsFormLabelResolution(application.customer_visa_application_data)) {
      return;
    }

    let cancelled = false;
    setLoadingLabels(true);

    axios
      .get(getVisaFormLabelsUrl(application.id))
      .then((res) => {
        if (cancelled) return;
        setFormEntries(
          getFormDisplayEntries(application.customer_visa_application_data, res.data?.form_field_labels || {})
        );
      })
      .catch(() => {
        if (!cancelled) setFormEntries(initialEntries);
      })
      .finally(() => {
        if (!cancelled) setLoadingLabels(false);
      });

    return () => {
      cancelled = true;
    };
  }, [application, filled]);

  if (!application) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Application Details</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>

        <div className="space-y-3 text-sm">
          {extraFields.map(({ label, value }) => (
            <div key={label}>
              <span className="font-medium text-gray-700">{label}:</span> {value}
            </div>
          ))}
          <div>
            <span className="font-medium text-gray-700">Passenger ID:</span> {application.passenger_id}
          </div>
          <div>
            <span className="font-medium text-gray-700">Applicant:</span>{' '}
            {filled ? getApplicantName(application.customer_visa_application_data) : 'Form pending'}
          </div>
          <div>
            <span className="font-medium text-gray-700">From:</span>{' '}
            {application.from_country_name || application.from_country}
          </div>
          <div>
            <span className="font-medium text-gray-700">To:</span>{' '}
            {application.to_country_name || application.to_country}
          </div>
          <div>
            <span className="font-medium text-gray-700">Visa Type:</span> {application.visa_type}
          </div>
          <div>
            <span className="font-medium text-gray-700">Status:</span>{' '}
            {formatStatusLabel(application.application_status)}
          </div>
          {(application.form_version_label || application.form_version > 0) && (
            <div>
              <span className="font-medium text-gray-700">Form Version:</span>{' '}
              {application.form_version_label || `v${application.form_version}`}
            </div>
          )}
          {application.status_description && (
            <div>
              <span className="font-medium text-gray-700">Status Description:</span>{' '}
              {application.status_description}
            </div>
          )}

          {filled ? (
            <div>
              <p className="font-medium text-gray-700 mb-2">Application Form Data</p>
              <div className="bg-gray-50 border border-gray-200 rounded-xl divide-y divide-gray-200">
                {loadingLabels ? (
                  <p className="px-4 py-3 text-gray-500">Loading field labels...</p>
                ) : formEntries.length > 0 ? (
                  formEntries.map((entry) => (
                    <div key={entry.key} className="px-4 py-3 grid grid-cols-1 sm:grid-cols-3 gap-1">
                      <span className="font-medium text-gray-600">{entry.label}</span>
                      <span className="sm:col-span-2 text-gray-900 break-words">{entry.value}</span>
                    </div>
                  ))
                ) : (
                  <p className="px-4 py-3 text-gray-500">No form field data available.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-amber-900">
              The employee has not completed the visa application form yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisaApplicationDetailModal;
